/* eslint-disable factorial/no-raw-html -- This is a custom table, not much we can use from the DS */
import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { BlankSlate, Box, Button, Link, Variants } from '@factorialco/deprecated-design-system'
import illustration from 'assets/images/competencies/career_path.png'
import { CareerPathPageDocument } from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { NewCompetencyModal } from 'modules/competencies/components/NewCompetencyModal'
import type { Role } from 'modules/job_catalog/hooks/useRole'
import { RoleTabs } from 'modules/job_catalog/pages/Role/RoleTabs'

import { AssignCompetenciesModal } from './AssignCompetenciesModal'
import CareerPathTable from './CareerPathTable'

interface OutletContext {
  role: Role
  canEditRole: boolean
  isLoading: boolean
  competenciesAvailable: boolean
}

function Component() {
  const hasNewCareerPath = hasFeature('dev_job_catalog_career_path')
  const [createCompetencyModalOpen, setCreateCompetencyModalOpen] = useState(false)
  const [assignCompetenciesModalOpen, setAssignCompetenciesModalOpen] = useState(false)
  const { role, canEditRole, competenciesAvailable } = useOutletContext<OutletContext>()
  const { data, loading, refetch } = useQuery(CareerPathPageDocument, {
    variables: { roleId: role.id },
  })
  const canManageCompetencies = data?.competencies._policies.competenciesManage.can || false
  const canEditJob = data?.jobCatalog.role?._policies?.editJob.can || false
  const levels = data?.jobCatalog.levelsConnection.nodes || []
  const expectations = data?.jobCatalog.role?.expectationsConnection.nodes || []

  const roleCompetencies = data?.jobCatalog.role?.competenciesConnection.nodes || []
  const globalCompetencies = data?.competencies.competenciesConnection.nodes || []
  const allCompetencies = [...roleCompetencies, ...globalCompetencies]

  const hasCompetencies = allCompetencies.length > 0
  const canAssignCompetency = canEditJob && canManageCompetencies
  const levelsWithoutDefault = levels
    .filter((level) => !level.isDefault)
    .sort((a, b) => a.order - b.order)

  const onAssignCompetencies = () => {
    track(trackingEvents.jobCatalog.jobRolePageAssignCompetencyStart)
    setAssignCompetenciesModalOpen(true)
  }

  const onCreateAndAssignCompetency = () => {
    track(trackingEvents.jobCatalog.jobRolePageCreateAndAssignCompetencyStart)
    setCreateCompetencyModalOpen(true)
  }

  if (loading) return null

  return (
    <AuthorizeUser authorizeFn={() => hasNewCareerPath && (competenciesAvailable || canEditRole)}>
      <Box width="full" gap="s24" overflowX="auto">
        <RoleTabs roleId={role.id} />

        <Box alignItems="flexEnd">
          {hasCompetencies && canAssignCompetency && (
            <AssignCompetencyButton
              type="secondary"
              onAssignCompetencies={onAssignCompetencies}
              onCreateAndAssignCompetency={onCreateAndAssignCompetency}
            />
          )}
        </Box>

        {!hasCompetencies && (
          <BlankSlate
            illustration="window-user"
            size="small"
            title={i18n.t('job_catalog.career_path.blank_slate.title')}
            description={i18n.t('job_catalog.career_path.blank_slate.description')}
          >
            <Box gap="s24" alignItems="center">
              {canAssignCompetency && (
                <AssignCompetencyButton
                  onAssignCompetencies={onAssignCompetencies}
                  onCreateAndAssignCompetency={onCreateAndAssignCompetency}
                />
              )}
              {/* TODO: add link to competencies help center article once we have it */}
              <Link.Href href="#" target="_blank">
                {i18n.t('competencies_v3.set_up_competencies.help_link')}
              </Link.Href>
              <img
                src={illustration}
                alt={i18n.t('job_catalog.career_path.blank_slate.illustration_alt')}
                title={i18n.t('job_catalog.career_path.blank_slate.illustration_alt')}
              />
            </Box>
          </BlankSlate>
        )}
        <Box overflowX="auto">
          {/* https://www.w3.org/WAI/EO/Drafts/tutorials/tables/id-headers/#example-2-table-with-three-headers-related-to-each-data-cell */}
          {hasCompetencies && (
            <CareerPathTable
              levels={levelsWithoutDefault}
              competencies={allCompetencies}
              expectations={expectations}
              role={role}
              refetch={refetch}
            />
          )}
        </Box>
      </Box>

      {createCompetencyModalOpen && (
        <NewCompetencyModal
          roleId={role.id}
          onSuccess={async () => {
            track(trackingEvents.jobCatalog.jobRoleAddAndAssignCompetencySubmit)
            refetch()
            setCreateCompetencyModalOpen(false)
          }}
          onClose={() => {
            track(trackingEvents.jobCatalog.jobRoleAddAndAssignCompetencyCancel)
            setCreateCompetencyModalOpen(false)
          }}
        />
      )}

      {assignCompetenciesModalOpen && (
        <AssignCompetenciesModal
          onSuccess={() => {
            refetch()
            setAssignCompetenciesModalOpen(false)
          }}
          onClose={() => {
            setAssignCompetenciesModalOpen(false)
          }}
        />
      )}
    </AuthorizeUser>
  )
}

export default Component

interface AssignCompetencyButtonProps {
  type?: Variants
  onAssignCompetencies: () => void
  onCreateAndAssignCompetency: () => void
}

const AssignCompetencyButton = ({
  type,
  onAssignCompetencies,
  onCreateAndAssignCompetency,
}: AssignCompetencyButtonProps) => {
  return (
    <Button.Split
      iconName="add"
      type={type}
      onClick={onAssignCompetencies}
      label={i18n.t('job_catalog.competencies.assign_competencies')}
    >
      <Button.Split.Option
        onClick={onCreateAndAssignCompetency}
        description={i18n.t('job_catalog.competencies.create_and_assign_description')}
      >
        {i18n.t('job_catalog.competencies.create_and_assign_title')}
      </Button.Split.Option>
    </Button.Split>
  )
}
